import { buildService } from '@rd-web-markets/shared/dist/services/service';
import serviceMethods from '@rd-web-markets/shared/dist/services/service_methods';
import { useShowToast } from '@rd-web-markets/shared/dist/util/toast/useToast';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
const api = buildService('/claims/{id}/aif_submissions', {
  only: ['create', 'get', 'all']
});
const useCreate = _ref => {
  let {
    claimId,
    setLoading,
    setAifReportCreationIdString,
    onSuccess,
    aif_claim_type
  } = _ref;
  const {
    t
  } = useTranslation();
  const showToast = useShowToast();
  const onSuccessCallback = useCallback(async function () {
    await onSuccess(...arguments);
    showToast({
      message: t('claimer_aif_submission_created')
    });
  }, [onSuccess, showToast, t]);
  const onError = useCallback(() => {
    showToast({
      message: t('could_not_create_aif_submission_in_claimer'),
      variant: 'warning'
    });
  }, [showToast, t]);
  const updateAdjustment = serviceMethods.useCreate({
    callback: useCallback(async () => {
      const {
        claimer_aif_report_creation_id
      } = await api.create(claimId, {
        aif_claim_type
      });
      setAifReportCreationIdString(claimer_aif_report_creation_id);
    }, [claimId, setAifReportCreationIdString, aif_claim_type]),
    setLoading,
    onSuccess: onSuccessCallback,
    onError
  });
  return updateAdjustment;
};
const useFetchAll = _ref2 => {
  let {
    claim,
    setLoading,
    aif_claim_type
  } = _ref2;
  const [currentSubmissionStatus, setCurrentSubmissionStatus] = useState({});
  const [submissionStatuses, setSubmissionStatuses, fetchStatuses] = serviceMethods.useFetch({
    callback: useCallback(async () => {
      if (!(claim !== null && claim !== void 0 && claim.claimer_aif_report_creation_id)) {
        return;
      }
      const {
        statuses
      } = await api.all(claim.id, {
        aif_claim_type
      });
      setCurrentSubmissionStatus(statuses.find(status => status.submission_id === claim.claimer_aif_report_creation_id));
      return statuses;
    }, [claim, aif_claim_type]),
    setLoading
  });
  return {
    submissionStatuses,
    setSubmissionStatuses,
    currentSubmissionStatus,
    setCurrentSubmissionStatus,
    fetchStatuses
  };
};
const useFetchAifReportData = _ref3 => {
  let {
    claim,
    setLoading,
    aif_claim_type
  } = _ref3;
  const setAifReportDataRef = useRef(null);
  const overrideHttpStatusErrorHandlers = useMemo(() => ({
    400: error => {
      setAifReportDataRef.current({
        errors: error.userFriendlyMessages
      });
    }
  }), []);
  const [aifReportData, setAifReportData, fetchAifReportData] = serviceMethods.useFetch({
    callback: useCallback(async () => {
      if (!claim) {
        return;
      }

      // This is a hack, to just get the show method of the controller to work, as we need to be able to display the
      // data that would be send to claimer. So we are not visualizing a real AIF submission - just the data that we 
      // would send to Claimer to create one - if the user wishes to do so.
      const data = await api.get(claim.id, claim.id, {
        aif_claim_type
      });
      return data;
    }, [claim, aif_claim_type]),
    setLoading,
    overrideHttpStatusErrorHandlers
  });
  setAifReportDataRef.current = setAifReportData;
  return {
    aifReportData,
    setAifReportData,
    fetchAifReportData
  };
};
const aifSubmissionsService = {
  useCreate,
  useFetchAll,
  useFetchAifReportData
};
export default aifSubmissionsService;